import {Component, ElementRef, HostListener, ViewChild, ViewEncapsulation} from '@angular/core';
import {MatAutocompleteSelectedEvent} from "@angular/material/autocomplete";
import {
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  forkJoin,
  Observable,
  ObservedValueOf,
  of,
  OperatorFunction,
  startWith,
  switchMap
} from "rxjs";
import {EquipmentService} from "../api/equipment.service";
import {ProjectService} from "../api/project.service";
import {RmaService} from "../api/rma.service";
import {debounceTime, map} from "rxjs/operators";
import {FormControl} from "@angular/forms";
import {Router} from "@angular/router";
import {projectUrl} from "../project/url.pipe";
import {EquipmentListResponse} from "../api/model/equipment";
import {Project} from "../api/model/project";
import {Rma} from "../api/model/rma";
import _ from "lodash";
import {PurchaseOrderService} from "../api/purchase-order.service";
import {PurchaseOrder} from "../api/model/purchase-order";
import {purchaseOrderPath} from "../purchase-order/list/list.component";

@Component({
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.sass'],
  encapsulation: ViewEncapsulation.None
})
export class GlobalSearchComponent {
  @ViewChild('search') searchElement!: ElementRef;

  searchResults: Observable<(SearchResultGroup | null)[]>;
  searchControl: FormControl = new FormControl([""]);

  constructor(
    private projectService: ProjectService,
    private equipmentService: EquipmentService,
    private purchaseOrderService: PurchaseOrderService,
    private rmaService: RmaService,
    private router: Router,
  ) {
    this.searchResults = this.searchControl.valueChanges
      .pipe(
        startWith(''),
        debounceTime(400),
        distinctUntilChanged(),
        switchMap(val => {
          return forkJoin([
            firstValueFrom(this._performEquipmentSearchQ(val)),
            firstValueFrom(this._performProjectSearchQ(val)),
            firstValueFrom(this._performPurchaseOrderSearchQ(val)),
            firstValueFrom(this._performRmaSearchQ(val)),
          ]).pipe(
            map(([equipmentResponse, projects, purchaseOrders, rmas]) => {
              console.log("GlobalSearchComponent loaded", equipmentResponse, projects, purchaseOrders, rmas);
              return [
                this.generateEquipmentResult(equipmentResponse, val),
                this.generateProjectResult(projects, equipmentResponse, val),
                this.generatePurchaseOrderResult(purchaseOrders, val),
                this.generateRmaResult(rmas),
              ]
            }));
        })
      );
  }

  @HostListener('window:keydown', ['$event'])
  detectGlobalHotkey($event: KeyboardEvent) {
    if ($event.key == "/") {
      console.log("GlobalSearchComponent / input detected", $event);
      if (!($event.target instanceof HTMLInputElement || $event.target instanceof HTMLTextAreaElement)) {
        $event.preventDefault();
        this.searchElement.nativeElement.focus();
      }
    }
  }

  searchOptionSelected($event: MatAutocompleteSelectedEvent) {
    const selection = $event.option.value as SingleSearchResult;
    if (selection.router) {
      console.log("GlobalSearchComponent searchOptionSelected by router", selection);
      this.router.navigate(selection.router.path, selection.router.fragment ? {fragment: selection.router.fragment} : undefined);
    } else if (selection.value) {
      console.log("GlobalSearchComponent searchOptionSelected by value", selection);
      this.router.navigateByUrl(selection.value);
    } else {
      return;
    }
    this.searchControl.setValue("", {emitEvent: true});
  }

  private _performEquipmentSearchQ(query: string): Observable<EquipmentListResponse | null> {
    return query.length === 0 ? of(null) : this.equipmentService.list({
      text: query,
      perPage: 0
    }).pipe(this.catchError("equipment"));
  }

  private generateEquipmentResult(value: EquipmentListResponse | null, query: string): SearchResultGroup {
    const matches = value?.equipment.length || 0;
    if (matches > 0) {
      return {
        label: `Equipment view ${matches} result${matches > 1 ? "s" : ""}`,
        value: `/equipment?q=${encodeURIComponent(query)}`,
        items: undefined,
      };
    } else {
      return {
        label: "Equipment not found",
        value: "",
        items: undefined,
      };
    }
  }

  private _performProjectSearchQ(query: string): Observable<Project[] | null> {
    return query.length === 0 ? of(null) : this.projectService.list(query).pipe(this.catchError("project"));
  }

  private generateProjectResult(projects: Project[] | null, equipmentResponse: EquipmentListResponse | null, query: string): SearchResultGroup {
    const projectResults = projects?.map(p => {
      return {
        label: `${p.name}`,
        value: projectUrl(p)
      }
    }) || [];

    const equipmentProjectResults = equipmentResponse && _.uniq(equipmentResponse.equipment.map(e => e.projectId) || [])
      .filter(pId => !projects?.find(p => p.id == pId))
      .map(pId => {
        return equipmentResponse.projects.find(p => p.id == pId)!!;
      }).map(p => {
        return {
          label: `${p.name}`,
          value: projectUrl(p) + "?q=" + encodeURIComponent(query)
        };
      }) || [];

    const matches = [...projectResults, ...equipmentProjectResults];
    if (matches.length > 0) {

      return {
        label: `Projects ${matches.length}`,
        value: `/equipment?q=${encodeURIComponent(query)}`,
        items: matches,
      };
    } else {
      return {
        label: "Projects not found",
        value: "",
        items: undefined,
      };
    }
  }


  private _performPurchaseOrderSearchQ(query: string): Observable<PurchaseOrder[] | null> {
    return query.length === 0 ? of(null) : this.purchaseOrderService.list([], undefined, false, query).pipe(this.catchError("project"));
  }

  private generatePurchaseOrderResult(purchaseOrders: PurchaseOrder[] | null, query: string): SearchResultGroup {
    const purchaseOrderResults = purchaseOrders?.map(po => {
      return {
        label: `${po.name}`,
        value: "",
        router: purchaseOrderPath(po)
      }
    }) || [];

    if (purchaseOrders && purchaseOrders.length > 0) {
      return {
        label: `Purchase Orders ${purchaseOrders.length}`,
        value: "",
        items: purchaseOrderResults,
      };
    } else {
      return {
        label: "Purchase Orders not found",
        value: "",
        items: undefined,
      };
    }
  }


  private _performRmaSearchQ(query: string): Observable<Rma[] | null> {
    return query.length === 0 ? of(null) : this.rmaService.list(query).pipe(this.catchError("rma"));

  }

  private generateRmaResult(rmas: Rma[] | null): SearchResultGroup {
    const matches = rmas?.length || 0;
    if (matches > 0) {
      return {
        label: `RMAs ${matches}`,
        value: undefined,
        items: rmas?.map(r => {
          return {
            label: `${r.rmaNumber}`,
            value: `/service/${r.rmaNumber}`
          }
        }),
      };
    } else {
      return {
        label: "RMAs not found",
        value: "",
        items: undefined,
      };
    }
  }

  private catchError(part: string): OperatorFunction<any, ObservedValueOf<Observable<null>>> {
    return catchError((err, caught) => {
      console.log("GlobalSearchComponent " + part + " err caught", err, caught);
      return of(null);
    });
  }
}

interface SearchResultGroup extends SingleSearchResult {
  label: string;
  items: SingleSearchResult[] | undefined;
  value: string | undefined;
}

interface SingleSearchResult {
  label: string;
  value: string | undefined;
  router?: { path: string[], fragment?: string };
}
